import {
    Component,
    OnInit,
    Output,
    EventEmitter,
    OnDestroy,
    HostListener,
    ChangeDetectorRef,
    ElementRef
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { Router, RouterModule } from '@angular/router'

import { MatDialog } from '@angular/material/dialog'
import { Subject, Subscription } from 'rxjs'

import { TranslateModule, TranslateService } from '@ngx-translate/core'

import { SharedModule } from '@@shared/shared.module'
import { ChangeUserPasswordPopupComponent } from '@@shared/componenets/change-user-password-popup/change-user-password-popup.component'
import { AddMembershipComponent } from '@@shared/componenets/add-membership/add-membership.component'

import { LOCAL_STORAG_KEYS } from '@@constants/local-storage-keys'
import { SECURITY_TAGS } from '@@constants/security-tags'

import getBoolean from '@@utils/getBoolean'
import cryptoDecryptData from '@@utils/cryptoDecryptData'

import { AppService } from '@@services/app.service'
import { BoxService } from '@@services/box.service'
import { AuthService } from '@@services/auth.service'
import { AlertService } from '@@services/alert.service'
import { GeneralService } from '@@services/general.service'
import { ToastService } from '@@services/toast.service'
import { FingerPrintService } from '@@services/fingerprint.service'
import { OnboardingPopupService } from '@@services/onboarding.service'

import { CUSTOM_EVENTS } from '@@constants/custom-events'

import { BranchManagementService } from '@@services/branch-management.service'
import { environment } from '../../../environments/environment'
import { LanguageSelectorComponent } from '../language-selector/language-selector.component'
import { PopupPanelComponent } from '@@shared/componenets/popup-panel/popup-panel.component'
import { BusinessSwitcherComponent } from '../business-switcher/business-switcher.component'

// Extend Window interface to include HubSpot types
declare global {
    interface Window {
        hsConversationsOnReady?: (() => void)[];
        HubSpotConversations?: {
            widget: () => {
                load: () => void;
                open: () => void;
                getConversations: () => any[];
                refresh: (options: {
                    identifyByEmail?: boolean;
                    openToNewThread?: boolean;
                    chatflowId?: string;
                }) => void;
            };
            on: (event: string, callback: (payload: any) => void) => void;
        };
        Intercom?: {
            (command: 'show' | 'shutdown' | 'update', settings?: any): void;
        };
        hsConversationsSettings?: {
            loadImmediately?: boolean;
            identificationEmail?: string;
            identificationToken?: string;
        };
        _hsq?: any;
    }
}

@Component({
    standalone: true,
    imports: [
        SharedModule,
        CommonModule,
        RouterModule,
        TranslateModule,
        LanguageSelectorComponent,
        PopupPanelComponent
    ],
    selector: 'in-portal-header',
    templateUrl: './portal-header.component.html',
    styleUrls: ['./portal-header.component.scss']
})
export class PortalHeaderComponent implements OnInit, OnDestroy {
    @Output() toggleMenu: EventEmitter<any> = new EventEmitter()

    static newSelectedBranch = new Subject<number>()
    static _newSelectedBranch =
        PortalHeaderComponent.newSelectedBranch.asObservable()

    private branchChangedSub!: Subscription

    public app: any
    public loggedInUser: string | null = localStorage.getItem(
        LOCAL_STORAG_KEYS.USER_NAME
    )
        ? localStorage.getItem(LOCAL_STORAG_KEYS.USER_NAME)
        : localStorage.getItem(LOCAL_STORAG_KEYS.COMPANY_NAME)

    singleSelectConfig: any = {
        labelField: 'name',
        valueField: 'id',
        searchField: ['name']
    }

    static withoutReload: boolean = false
    availableBranches: any
    hasFPrint: boolean = false
    branches?: Array<any>
    idSelectedBranch = localStorage.getItem(
        LOCAL_STORAG_KEYS.SELECTED_BRANCH_ID
    )
    selectedBranch = this.idSelectedBranch ? +this.idSelectedBranch : null

    // Branch dropdown state
    isBranchDropdownOpen = false
    get selectedBranchId(): number | null {
        return this.selectedBranch
    }
    get selectedBranchName(): string {
        const branch = this.branches?.find((b) => b.id === this.selectedBranch)
        return branch?.name || ''
    }

    // Profile panel state
    isProfilePanelOpen = false
    isHelpPanelOpen = false
    private helpFallbackTimer: any

    token?: string
    settingsMenu: Array<any> = []
    companySettingsSecTags = [
        SECURITY_TAGS.MANAGE_BASIC_SETTINGS,
        SECURITY_TAGS.MANAGE_STAFF,
        SECURITY_TAGS.MANAGE_BRANCHES,
        SECURITY_TAGS.MANAGE_ONLINE_BOOKING,
        SECURITY_TAGS.MANAGE_PACKAGES_SERVICES,
        SECURITY_TAGS.MANAGE_PAYMENT_METHODS,
        SECURITY_TAGS.MANAGE_DISCOUNTS,
        SECURITY_TAGS.MANAGE_INVENTORY
    ]
    canViewEvents: boolean = false
    canViewFields: boolean = false
    smartSearchData: any
    isMobile?: boolean
    fprintEnabled = false
    onboardingEnabled: boolean = false
    showOnboarding: boolean = false

    automaticBillingInfoDismissed = false
    public subscriptionWarning: any
    public subscriptionSecondWarning: boolean = false
    public canViewIn2SubscriptionPage = false
    public canManageIn2SubscriptionPage = false
    public headerStyle = ''

    // Grace period properties
    public gracePeriodMessage: string = '';
    public showGracePeriodWarning: boolean = false;

    constructor(
        private router: Router,
        private appService: AppService,
        private boxService: BoxService,
        private authService: AuthService,
        private alertService: AlertService,
        private toastService: ToastService,
        private onboardingPopupService: OnboardingPopupService,
        private translate: TranslateService,
        private cdr: ChangeDetectorRef,
        private elementRef: ElementRef,
        private branchManagementService: BranchManagementService,
        public dialog: MatDialog
    ) {}

    toggleProfilePanel(event: MouseEvent) {
        event.stopPropagation()
        this.closeHelpPanel()
        this.isProfilePanelOpen = !this.isProfilePanelOpen
    }

    closeProfilePanel() {
        this.isProfilePanelOpen = false
    }

    toggleHelpPanel(event: MouseEvent) {
        event.stopPropagation()
        this.closeProfilePanel()
        this.isHelpPanelOpen = !this.isHelpPanelOpen
    }

    closeHelpPanel() {
        this.isHelpPanelOpen = false
    }

    @HostListener('document:keydown.escape')
    onEscapeKey() {
        this.closeProfilePanel()
        this.closeHelpPanel()
        this.closeBranchDropdown()
    }

    @HostListener('document:pointerdown', ['$event'])
    onDocumentClick(event: Event) {
        const branchSelector = this.elementRef.nativeElement.querySelector('.header-branch-selector')
        if (branchSelector && !branchSelector.contains(event.target)) {
            this.closeBranchDropdown()
        }
    }

    toggleBranchDropdown() {
        this.isBranchDropdownOpen = !this.isBranchDropdownOpen
    }

    closeBranchDropdown() {
        this.isBranchDropdownOpen = false
    }

    getIsMobile() {
        const width = Math.max(
            document.documentElement.clientWidth,
            window.innerWidth || 0
        )
        return width <= 990
    }

    ngOnInit() {
        this.automaticBillingInfoDismissed = getBoolean(
            sessionStorage.getItem(
                LOCAL_STORAG_KEYS.AUTOMATIC_BILLING_IFO_DISMISSED
            )
        )

        this.canViewIn2SubscriptionPage = this.authService.hasSecurityTag(
            SECURITY_TAGS.VIEW_IN2_SUBSCRIPTION
        )

        this.canManageIn2SubscriptionPage = this.authService.hasSecurityTag(
            SECURITY_TAGS.MANAGE_IN2_SUBSCRIPTION
        )

        GeneralService.customEvent.subscribe((temp) => {
            if (
                temp &&
                temp === CUSTOM_EVENTS.IN2_SUSCRIPTION_WARNING_MESSAGE_CHNAGED
            ) {
                this.checkIn2SubscriptionWarning()
            }
        })

        GeneralService.tenantChangeEvent.subscribe((res) => {
            this.checkIn2SubscriptionWarning()
            this.checkGracePeriodWarning()
        })

        let portalLayoutcomp = this
        setInterval(() => {
            portalLayoutcomp.checkIn2SubscriptionWarning()
        }, 5000)

        // Check grace period warning on init and tenant change
        this.checkGracePeriodWarning()

        this.onboardingEnabled =
            environment.apiUrl != 'https://app.joinin2.com/'

        this.isMobile = this.getIsMobile()

        this.fprintEnabled = FingerPrintService.isFingerPrintEnabled()

        this.getAvailableBranches()

        this.getUserAssignedBranches()

        this.branchChangedSub = BranchManagementService.branchChanged$.subscribe(
            (branchId) => PortalHeaderComponent.newSelectedBranch.next(branchId)
        )

        PortalHeaderComponent._newSelectedBranch.subscribe((branchId: number) => {
            this.selectedBranch = branchId
        })

        this.canViewFields = this.authService.hasSecurityTag(
            SECURITY_TAGS.FIELDS_VIEW
        )

        const thisclass = this

        setTimeout(() => {
            this.settingsMenu = [
                {
                    label: 'layout.portal-header.online-transactions',
                    icon: 'book-open',
                    routerLink: '/portal/in2statement',
                    checkVisibility: true,
                    visible: this.authService.userIsOwner()
                },
                {
                    label: 'layout.portal-header.product-updates',
                    icon: 'megaphone-rays',
                    iconSize: 20,
                    externalUrl: environment.productUpdatesUrl,
                    openInNewTab: true,
                    checkVisibility: false
                },
                {
                    label: 'layout.portal-header.sync-to-zoho',
                    icon: 'chevrons-right',
                    routerLink: '/zohoSync',
                    openInNewTab: true,
                    securityTags: 'ZOHO_SYNC'
                }
            ]
        }, 200)

        this.fetchBranches()
        BusinessSwitcherComponent._newSelectedBranch.subscribe((branchId: number) => {
            this.selectedBranch = branchId
            this.cdr.detectChanges()
        })

        this.updateIsMobile()
        window.addEventListener('resize', this.updateIsMobile.bind(this))

        this.smartSearchData = {
            searchPlaceHolder: 'layout.portal-header.search-client',
            resetOnSelection: false,
            service: this.appService,
            apiFunction: 'searchClients',
            dataPath: 'list',
            searchboxClass: 'dashboardclientsearch',
            optionIconClass: 'person',
            actionUrl: (client: any) => {
                const actions = [
                    {
                        icon: 'person',
                        label: this.translate.instant(
                            'layout.portal-header.view-profile'
                        ),
                        class: '',
                        color: '#FF7F50',
                        action: '/portal/clients/client/' + client.id
                    },
                    {
                        icon: 'add_circle',
                        label: this.translate.instant(
                            'layout.portal-header.buy-membership'
                        ),
                        class: '',
                        color: 'FF7F50',

                        action: (client: any) => {
                            thisclass.addMembership(client)
                        }
                    }
                ]
                return actions
            }
        }
    }

    ngOnDestroy() {
        clearTimeout(this.helpFallbackTimer);
        window.removeEventListener('resize', this.updateIsMobile.bind(this))
        this.branchChangedSub?.unsubscribe()
    }

    updateIsMobile() {
        this.isMobile = this.getIsMobile()
    }

    changePassword() {
        let width = '400px'
        this.boxService
            .boxPopup({
                maxWidth: width,
                width: width,
                disableClose: true,
                data: {
                    showCloseButton: true,
                    closeOnConfirm: false,
                    closeOnCancel: false,
                    status: {
                        confirm: false,
                        cancel: false,
                        delete: false,
                        custom: false
                    },
                    label: {
                        title: this.translate.instant(
                            'layout.portal-header.change-your-password'
                        ),
                        confirm: '',
                        cancel: '',
                        delete: '',
                        custom: ''
                    },
                    class: {
                        title: '',
                        confirm: '',
                        cancel: '',
                        delete: '',
                        custom: '',
                        text: ''
                    },
                    content: {
                        isOnline: false,
                        text: ''
                    },
                    component: ChangeUserPasswordPopupComponent
                }
            })
            .subscribe((result) => {})
    }

    fetchBranches() {
        this.appService.getUserAssignedBranches().subscribe((response: any) => {
            if (response?.branches) {
                this.branches = response.branches
            }
            const id = localStorage.getItem(LOCAL_STORAG_KEYS.SELECTED_BRANCH_ID)
            this.selectedBranch = id ? +id : null
        })
    }

    openHelp() {
        clearTimeout(this.helpFallbackTimer);
        const hubSpotConversations: any = window['HubSpotConversations'];
        if (hubSpotConversations && environment.isLive) {
            this.authService.updateThirdpartyIntegrations(true);
            // Fallback if widget doesn't open within 4s
            this.helpFallbackTimer = setTimeout(() => {
                const chatFrame = document.getElementById('hubspot-conversations-iframe');
                if (!chatFrame || chatFrame.offsetHeight === 0) {
                    this.openSupportFallback();
                }
            }, 4000);
        } else {
            this.openSupportFallback();
        }
    }

    private openSupportFallback() {
        window.location.href = 'mailto:support@joinin2.com';
    }

    openRequestFeature() {
        window.open('https://requests.joinin2.com', '_blank');
    }

    openRequestAddOn() {
        window.open('https://433nb.share.hsforms.com/2x7Z9Ul4iSoiTIjaFiqcTXQ', '_blank');
    }

    addMembership(client: any) {
        this.boxService
            .boxPopup({
                width: '1100px',
                data: {
                    closeOnConfirm: false,
                    closeOnCancel: false,
                    showCloseButton: true,
                    status: {
                        confirm: true,
                        cancel: false,
                        delete: false,
                        custom: true
                    },
                    label: {
                        title: this.translate.instant(
                            'layout.portal-header.add-membership'
                        ),
                        confirm: this.translate.instant(
                            'layout.portal-header.confirm'
                        ),
                        cancel: this.translate.instant(
                            'layout.portal-header.back'
                        ),
                        delete: '',
                        custom: this.translate.instant(
                            'layout.portal-header.save-incomplete'
                        )
                    },
                    class: {
                        title: '',
                        confirm: 'in-blue-button',
                        cancel: 'in-grey-button',
                        delete: 'in-red-button',
                        custom: 'in-transparent-button',
                        text: ''
                    },
                    content: {
                        clientName: client.name,
                        type: 'check',
                        idClient: client.id,
                        showRegularAndPrivatePackages: true
                    },
                    component: AddMembershipComponent
                }
            })
            .subscribe((res) => {
                this.boxService.unsubscribeFnCustom()
            })
    }

    getAvailableBranches() {
        const idBranches = localStorage.getItem(LOCAL_STORAG_KEYS.BRANCHES_ID)
        if (idBranches) {
            this.availableBranches = idBranches.split(',')
        }
        this.hasFPrint = FingerPrintService.isFingerPrintEnabled()
        this.branchChanged()
    }

    getUserAssignedBranches(tenantChanged?: any) {
        this.branches = []

        this.appService.getUserAssignedBranches().subscribe((response) => {
            setTimeout(() => {
                if (response.branches) this.branches = response.branches

                localStorage.setItem(
                    LOCAL_STORAG_KEYS.TENANT_BRANCHES_FULL,
                    JSON.stringify(this.branches)
                )

                if (
                    localStorage.getItem(LOCAL_STORAG_KEYS.TENANT_ID) &&
                    (!this.branches || this.branches.length == 0)
                )
                    this.router
                        .navigateByUrl('/portal/company/selection')
                        .catch((err) => {
                            console.error(err)
                        })

                const idSelectedBranch = localStorage.getItem(
                    LOCAL_STORAG_KEYS.SELECTED_BRANCH_ID
                )
                if (idSelectedBranch) {
                    this.selectedBranch = +idSelectedBranch

                    let selectedBranch = this.branches?.filter(
                        (b) => b.id == this.selectedBranch
                    )[0]

                    this.fillLocalStorageBranchConfigs(selectedBranch)

                    if (tenantChanged)
                        PortalHeaderComponent.newSelectedBranch.next(
                            this.selectedBranch
                        )
                }
            }, 100)
        })
    }

    private fillLocalStorageBranchConfigs(selectedBranch: any) {
        setTimeout(() => {
            this.performFillLocalStorageBranchConfigs(selectedBranch)
        }, 1000)

        setTimeout(() => {
            this.performFillLocalStorageBranchConfigs(selectedBranch)
        }, 3000)
    }

    private performFillLocalStorageBranchConfigs(selectedBranch: any) {
        if (selectedBranch.localServerAddress) {
            localStorage.setItem(
                LOCAL_STORAG_KEYS.LOCAL_SERVER_ADDRESS,
                selectedBranch.localServerAddress
            )
        } else {
            localStorage.removeItem(LOCAL_STORAG_KEYS.LOCAL_SERVER_ADDRESS)
        }

        if (selectedBranch.localAccessWindow) {
            localStorage.setItem(
                LOCAL_STORAG_KEYS.LOCAL_ACCESS_WINDOW,
                selectedBranch.localAccessWindow
            )
        } else {
            localStorage.removeItem(LOCAL_STORAG_KEYS.LOCAL_ACCESS_WINDOW)
        }
    }

    onBranchChange(branchId: number) {
        this.changeSelectedBranch(branchId)
    }

    changeSelectedBranch(event: any) {
        if (!event || event == '') {
            return
        }

        let branchTenant: {
            [key: string]: string
        } = {}
        this.branches?.forEach((branch) => {
            branchTenant[branch.id] = branch.idTenant
        })

        let selectedBranch = this.branches?.filter((b) => b.id == event)[0]

        let idTenant = branchTenant[event]
        localStorage.setItem(LOCAL_STORAG_KEYS.TENANT_ID, idTenant)
        this.branchManagementService.setCurrentBranchId(+event)

        this.selectedBranch = event
        this.fillLocalStorageBranchConfigs(selectedBranch)

        PortalHeaderComponent.newSelectedBranch.next(event)
        window.location.reload()
    }

    branchChanged() {
        GeneralService.securityTagsDirectiveEvent.emit(true)
    }

    openOnboarding() {
        this.showOnboarding = true
        this.onboardingPopupService.open()
    }

    logout() {
        this.authService.logout()
    }

    toggleNavigation() {
        this.toggleMenu.emit(true)
    }

    settingsMenuItemClicked(settingMenuItem: any) {
        if (settingMenuItem.openInNewTab) {
            window.open(
                window.location.origin + settingMenuItem.routerLink,
                '_blank'
            )
        }
    }

    onSubscriptionWarningClick($event: MouseEvent) {
        // Don't navigate if clicking the close button
        const target = $event.target as HTMLElement
        if (target.closest('button.btn-close') || target.closest('button.close') || target.closest('button[aria-label="Close"]')) {
            return
        }

        if (
            this.canViewIn2SubscriptionPage ||
            this.canManageIn2SubscriptionPage
        ) {
            this.router
                .navigateByUrl('/portal/settings/In2Subscription')
                .catch((err) => {
                    console.error(err)
                })
        }
    }

    dismissAutomaticBillingInfo() {
        sessionStorage.setItem(
            LOCAL_STORAG_KEYS.AUTOMATIC_BILLING_IFO_DISMISSED,
            'true'
        )
        this.automaticBillingInfoDismissed = true
    }

    private checkIn2SubscriptionWarning() {
        let headerHeight = 65
        this.headerStyle = ''

        const in2SubscriptionWarningMessage = localStorage.getItem(
            LOCAL_STORAG_KEYS.IN2_SUBSCRIPTION_WARNING_MESSAGE
        )
        if (
            in2SubscriptionWarningMessage &&
            in2SubscriptionWarningMessage.length > 0
        ) {
            this.subscriptionWarning = cryptoDecryptData(
                in2SubscriptionWarningMessage
            )
            let isSecondWarning = cryptoDecryptData(
                localStorage.getItem(
                    LOCAL_STORAG_KEYS.IN2_SUBSCRIPTION_SECOND_WARNING
                )
            )
            this.subscriptionSecondWarning = getBoolean(isSecondWarning)
            headerHeight += 60
        } else {
            this.subscriptionWarning = null
            this.subscriptionSecondWarning = false
        }

        if (!this.automaticBillingInfoDismissed) {
            headerHeight += 60
        }

        this.headerStyle
    }

    private checkGracePeriodWarning() {
        // Check if user is in grace period and get the message
        const hasSubscriptionAccess = AuthService.isSubscriptionOnlyAccess();

        if (hasSubscriptionAccess) {
            this.gracePeriodMessage = AuthService.getGracePeriodMessage();
            this.showGracePeriodWarning = true;
        } else {
            this.gracePeriodMessage = '';
            this.showGracePeriodWarning = false;
        }
    }

}
